import React, { useState, useEffect, useRef } from 'react'
import PropTypes from 'prop-types'
import { getLocation } from 'components/context/SearchContainer'
import { map } from 'lodash'
import './index.css'

const useClickOutside = (ref, callback) => {
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (ref.current && !ref.current.contains(event.target)) {
        callback()
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [ref, callback])
}

const ReactMapboxAutocomplete = (props) => {
  const [error, setError] = useState(false)
  const [errorMsg, setErrorMsg] = useState('')
  const [queryResults, setQueryResults] = useState([])
  const [publicKey] = useState(props.publicKey)
  const [resetSearch] = useState(props.resetSearch || false)
  const containerRef = useRef(null)

  const setInputValue = () => {
    const url = new URL(window.location.href)
    const params = new URLSearchParams(url.href)
    const location = params.get('location')
    const currentLocation = getLocation('location')
    let mapboxSearchInput = document.getElementById('mapbox-main-search-input')
    if(location || currentLocation) {
      mapboxSearchInput.value = location || currentLocation
    }
  }

  useEffect(() => {
    setInputValue()
  }, [])

  useClickOutside(containerRef, () => {
    setInputValue()
    setQueryResults([])
  })

  const updateQuery = (event) => {
    let mapboxSearchInput = document.getElementById('mapbox-main-search-input')
    mapboxSearchInput.value = event.target.value
    const header = { 'Content-Type': 'application/json' }
    let typesQP = ['address', 'postcode', 'place'].join(',')
    if (props.types && props.types.length > 0) {
      typesQP = props.types.join(',')
    }

    let path = `https://api.mapbox.com/geocoding/v5/mapbox.places/${event.target.value}.json?&types=${typesQP}&access_token=${publicKey}`
    if (props.country) {
      path = `${path}&country=${props.country}`
    }

    if (event.target.value?.length >= 2) {
      fetch(path, { headers: header })
        .then(res => {
          if (!res.ok) throw Error(res.statusText)
          return res.json()
        })
        .then(json => {
          setError(false)
          setQueryResults(json.features)
        })
        .catch(err => {
          setError(true)
          setErrorMsg('There was a problem retrieving data from mapbox')
          setQueryResults([])
        })
    } else {
      setError(false)
      setQueryResults([])
    }
  }

  const resetSearchFunc = () => {
    if (resetSearch) {
      setQueryResults([])
    } else {
      setQueryResults([])
    }
  }

  const onSuggestionSelect = (event) => {
    let mapboxSearchInput = document.getElementById('mapbox-main-search-input')
    mapboxSearchInput.value = event.target.getAttribute('data-suggestion')

    props.onSuggestionSelect(
      event.target.getAttribute('data-suggestion'),
      event.target.getAttribute('data-lat'),
      event.target.getAttribute('data-lng'),
      event.target.getAttribute('data-text'),
      { placeName: event.target.getAttribute('data-placename') }
    )
  }

  const getDropdowns = (place, i) => {
    const placeName = place.place_name
    const placeArray = placeName?.split(',')
    const region = place.context.find(context => context.id.includes('region'))
    const city = place.context.find(context => context.id.includes('place'))
    const shortCode = region ? region.short_code?.split('-') : null
    const shortCodeAbbr = shortCode?.reverse()?.map(code => code)?.join(', ')
    const dataSuggestion = `${placeArray[0]}${city ? ' ' + city.text : ''} ${shortCodeAbbr}`
    
    if (placeArray?.length > 1) {
      return (
        <div className={`react-mapbox-ac-suggestion ${props.searchResultsClass}`}
          onClick={onSuggestionSelect}
          key={i}
          data-suggestion={dataSuggestion}
          data-lng={place.center[0]}
          data-lat={place.center[1]}
          data-text={place.text}
          data-placename={placeName}
        >
          <div className="place-firstName"
            data-suggestion={dataSuggestion}
            data-lng={place.center[0]}
            data-lat={place.center[1]}
            data-text={place.text}
            data-placename={placeName}
          >
            {placeArray.slice(0, 2).join(', ')}&nbsp;
          </div>
          <span
            data-suggestion={dataSuggestion}
            data-lng={place.center[0]}
            data-lat={place.center[1]}
            data-placename={placeName}
          >
            {shortCodeAbbr ? shortCodeAbbr : placeArray?.slice(1)?.join(',')}
          </span>
        </div>
      )
    } else {
      return (
        <div className='react-mapbox-ac-suggestion'
          onClick={onSuggestionSelect}
          key={i}
          data-suggestion={placeArray[0]}
          data-lng={place.center[0]}
          data-lat={place.center[1]}
          data-text={place.text}
          data-placename={placeName}
        >
          <div className="place-firstName"
            data-suggestion={placeArray[0]}
            data-lng={place.center[0]}
            data-lat={place.center[1]}
            data-text={place.text}
            data-placename={placeName}
          >
            {placeArray[0]}
          </div>
        </div>
      )
    }
  }

  return (
    <div ref={containerRef}>
      <input
        placeholder={props.placeholder || 'Search'}
        id="mapbox-main-search-input"
        className={props.inputClass ? props.inputClass + ' react-mapbox-ac-input' : 'react-mapbox-ac-input'}
        onChange={updateQuery}
        onClick={updateQuery}
        type='text'
        autoComplete='off'
      />
      <span>
        <div className='react-mapbox-ac-menu'
          style={queryResults.length > 0 || error ? { display: 'block' } : { display: 'none' }}
          onClick={resetSearchFunc}
        >
          {map(queryResults, (place, i) => getDropdowns(place, i))}
          {error && <div className="react-mapbox-ac-suggestion">{errorMsg}</div>}
        </div>
      </span>
    </div>
  )
}

ReactMapboxAutocomplete.propTypes = {
  inputClass: PropTypes.string,
  publicKey: PropTypes.string.isRequired,
  placeholder: PropTypes.string,
  onSuggestionSelect: PropTypes.func.isRequired,
  country: PropTypes.string,
  query: PropTypes.string,
  resetSearch: PropTypes.bool,
  types: PropTypes.arrayOf(PropTypes.string),
  searchResultsClass: PropTypes.string
}

export default ReactMapboxAutocomplete
